import { isTokenExpired } from './jwtUtils';
import { env } from '../config/env';
import { store } from '../store';
import { logout } from '../store/slices/authSlice';

/**
 * Creates a fetch interceptor for API calls
 * @param {Function} originalFetch - The original fetch function
 * @returns {Function} - The intercepted fetch function
 */
export const createFetchInterceptor = (originalFetch) => {
  return async (...args) => {
    const [url, options = {}] = args;
    
    // Only intercept calls to our API
    if (!url.startsWith(env.apiUrl)) {
      return originalFetch(...args);
    }
    
    // Get the current auth state
    const state = store.getState();
    const { token, refreshToken } = state.auth;
    
    // If we have a token and it's not a login/refresh request, add it to the headers
    if (token && !url.includes('/auth/token') && !url.includes('/auth/refresh')) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        // If we have a refresh token, try to refresh
        if (refreshToken) {
          try {
            // Make a refresh token request
            const refreshResponse = await originalFetch(`${env.apiUrl}/auth/refresh`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ refresh_token: refreshToken }),
            });
            
            if (!refreshResponse.ok) {
              // If refresh fails, log the user out
              store.dispatch(logout());
              throw new Error('Session expired. Please login again.');
            }
            
            // Get the new tokens
            const refreshData = await refreshResponse.json();
            
            // Update the auth state with the new token
            store.dispatch({
              type: 'auth/tokenRefreshed',
              payload: refreshData,
            });
            
            // Continue with the original request using the new token
            const newOptions = {
              ...options,
              headers: {
                ...options.headers,
                Authorization: `Bearer ${refreshData.access_token}`,
              },
            };
            
            return originalFetch(url, newOptions);
          } catch (error) {
            // If refresh fails, log the user out
            store.dispatch(logout());
            throw new Error('Session expired. Please login again.');
          }
        } else {
          // No refresh token, log the user out
          store.dispatch(logout());
          throw new Error('Session expired. Please login again.');
        }
      }
      
      // Token is valid, add it to the headers
      const newOptions = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${token}`,
        },
      };
      
      return originalFetch(url, newOptions);
    }
    
    // For login/refresh requests or requests without a token, proceed normally
    return originalFetch(...args);
  };
};

/**
 * Initialize the fetch interceptor
 */
export const initializeInterceptor = () => {
  // Save the original fetch
  const originalFetch = window.fetch;
  
  // Replace fetch with our interceptor
  window.fetch = createFetchInterceptor(originalFetch);
};
